// Copyright (c) Docugami, Inc. All rights reserved.

import { TextEntryDialog } from "components/shell/utils/TextEntryDialog";
import { useWaitCursor } from "hooks/WaitCursor";
import { useGetDocsetDescriptionQuery, useUpdateDocsetDescriptionMutation, useDeleteDocsetDescriptionMutation } from "store/frontend/frontendSlice";
import { DocSetDescriptionCancelledSchema, DocSetDescriptionConfirmedSchema, DocSetDescriptionRevertedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";


interface DocsetDescriptionDialogProps {
    docsetId: string;
    onClose: () => void;
}

export const DocsetDescriptionDialog = (props: DocsetDescriptionDialogProps): JSX.Element => {
    const { data: description, isLoading: queryLoading } = useGetDocsetDescriptionQuery(props.docsetId);
    const [triggerUpdate, updateStatus] = useUpdateDocsetDescriptionMutation();
    const [triggerDelete, deleteStatus] = useDeleteDocsetDescriptionMutation();
    useWaitCursor(updateStatus.isLoading || deleteStatus.isLoading || queryLoading);

    async function confirm(value: string) {
        Telemetry.trackEvent<DocSetDescriptionConfirmedSchema>({
            docSetId: props.docsetId,
            eventName: TelemetryEvent.DocSetDescriptionConfirmed
        });

        value = value.trim();
        if (!!value && value !== description?.description) {
            await triggerUpdate({
                docsetId: props.docsetId,
                description: value})
        }
        props.onClose();
    }

    function dismiss() {
        Telemetry.trackEvent<DocSetDescriptionCancelledSchema>({
            docSetId: props.docsetId,
            eventName: TelemetryEvent.DocSetDescriptionCancelled
        });
        props.onClose();
    }

    async function revert() {
        Telemetry.trackEvent<DocSetDescriptionRevertedSchema>({
            docSetId: props.docsetId,
            eventName: TelemetryEvent.DocSetDescriptionReverted
        });
        await triggerDelete(props.docsetId);
        props.onClose();
    }

    function getOriginalText(): string {
        return !!description?.userDescription ? description?.userDescription : description?.description ?? '';
    }

    function validateInput(value: string): boolean {
        const trimmed = value.trim();
        return trimmed.length > 0 && trimmed.length <= 2000 && trimmed !== getOriginalText().trim();
    }

    return (
        queryLoading ? <></> :
        <TextEntryDialog
            hidden={false}
            originalText={getOriginalText()}
            placeholderText={!!!description?.description ? "A description has not yet been generated.  It will be generated when new documents are uploaded." : "Enter a description, or use the Revert button to use the system generated description."}
            title="Doc Set Description"
            description="The following description will be used when considering if an uploaded document should be assigned to this doc set."
            multiline={true}
            rows={4}
            width="800px"
            confirmButtonText="Update"
            dismissButtonText="Close"
            revertButtonText={!!description?.userDescription ? "Revert" : undefined}
            confirmedButtonDisabled={value => !validateInput(value)}
            confirmedCallback={value => confirm(value)}
            dismissedCallback={() => dismiss()}
            revertCallback={() => revert()}
            actionInProgress={updateStatus.isLoading || deleteStatus.isLoading}/>
    );
}
